import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  immobilierApi,
  type AjoutAcomptePayload, type CreateLotImmoPayload, type CreateProgrammePayload, type CreateReservationVefaPayload,
  type LotImmoListParams, type ProgrammeListParams, type ReservationVefaListParams,
  type UpdateLotImmoPayload, type UpdateProgrammePayload, type UpdateReservationVefaPayload,
} from '@/api/immobilier';

const PROGRAMMES_KEY  = ['operations', 'immobilier', 'programmes']  as const;
const LOTS_IMMO_KEY   = ['operations', 'immobilier', 'lots']        as const;
const RESERVATIONS_KEY = ['operations', 'immobilier', 'reservations'] as const;

// Programmes
export const usePrograms = (params: ProgrammeListParams = {}) =>
  useQuery({ queryKey: [...PROGRAMMES_KEY, params], queryFn: () => immobilierApi.listProgrammes(params), staleTime: 30_000 });
export const useProgram = (id: string | undefined) =>
  useQuery({ queryKey: [...PROGRAMMES_KEY, id], queryFn: () => immobilierApi.getProgramme(id!), enabled: !!id, staleTime: 15_000 });
export const useCreateProgram = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateProgrammePayload) => immobilierApi.createProgramme(p),
    onSuccess:  () => qc.invalidateQueries({ queryKey: PROGRAMMES_KEY }),
  });
};
export const useUpdateProgram = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateProgrammePayload }) => immobilierApi.updateProgramme(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: PROGRAMMES_KEY }),
  });
};
export const useDeleteProgram = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => immobilierApi.deleteProgramme(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: PROGRAMMES_KEY }),
  });
};

// Lots immo
export const useLotsImmo = (params: LotImmoListParams = {}) =>
  useQuery({ queryKey: [...LOTS_IMMO_KEY, params], queryFn: () => immobilierApi.listLots(params), staleTime: 30_000 });
export const useLotImmo = (id: string | undefined) =>
  useQuery({ queryKey: [...LOTS_IMMO_KEY, id], queryFn: () => immobilierApi.getLot(id!), enabled: !!id, staleTime: 15_000 });
export const useCreateLotImmo = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateLotImmoPayload) => immobilierApi.createLot(p),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: LOTS_IMMO_KEY });
      qc.invalidateQueries({ queryKey: PROGRAMMES_KEY });
    },
  });
};
export const useUpdateLotImmo = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateLotImmoPayload }) => immobilierApi.updateLot(id, payload),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: LOTS_IMMO_KEY });
      qc.invalidateQueries({ queryKey: PROGRAMMES_KEY });
    },
  });
};
export const useDeleteLotImmo = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => immobilierApi.deleteLot(id),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: LOTS_IMMO_KEY });
      qc.invalidateQueries({ queryKey: PROGRAMMES_KEY });
    },
  });
};

// Réservations VEFA
export const useReservationsVefa = (params: ReservationVefaListParams = {}) =>
  useQuery({ queryKey: [...RESERVATIONS_KEY, params], queryFn: () => immobilierApi.listReservations(params), staleTime: 30_000 });
export const useReservationVefa = (id: string | undefined) =>
  useQuery({ queryKey: [...RESERVATIONS_KEY, id], queryFn: () => immobilierApi.getReservation(id!), enabled: !!id, staleTime: 15_000 });
export const useCreateReservationVefa = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateReservationVefaPayload) => immobilierApi.createReservation(p),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: RESERVATIONS_KEY });
      qc.invalidateQueries({ queryKey: LOTS_IMMO_KEY }); // statut lot peut changer
    },
  });
};
export const useUpdateReservationVefa = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateReservationVefaPayload }) => immobilierApi.updateReservation(id, payload),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: RESERVATIONS_KEY });
      qc.invalidateQueries({ queryKey: LOTS_IMMO_KEY });
    },
  });
};
export const useAjouterAcompte = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AjoutAcomptePayload }) => immobilierApi.ajouterAcompte(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: RESERVATIONS_KEY }),
  });
};
export const useDeleteReservationVefa = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => immobilierApi.deleteReservation(id),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: RESERVATIONS_KEY });
      qc.invalidateQueries({ queryKey: LOTS_IMMO_KEY });
    },
  });
};
